import { StandardEvents } from '@shopify/events';

// Progressive enhancement only. Product selection, pricing, cart, and checkout stay with Shopify.
const configNode = document.getElementById('APStoreConfig');
const config = configNode ? JSON.parse(configNode.textContent) : {};
const rootPath = String(config.root || '/').replace(/\/?$/, '/');
const apiUrl = path => new URL(rootPath + path, window.location.origin).toString();
const storageKey = 'attorney-problems-favorites-v1';
const handlePattern = /^[a-z0-9][a-z0-9-]{0,254}$/;
const limit = Math.max(1, Math.min(60, Number(config.savedLimit) || 60));
const readSaved = () => {
  try {
    const value = JSON.parse(localStorage.getItem(storageKey) || '[]');
    return Array.isArray(value) ? [...new Set(value.filter(h => typeof h === 'string' && handlePattern.test(h)))].slice(0, limit) : [];
  } catch { return []; }
};
let saved = readSaved();
let toastTimer;
function announce(message) {
  let status = document.getElementById('APToast');
  if (!status) {
    status = document.createElement('div');
    status.id = 'APToast';
    status.className = 'ap-toast';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    document.body.append(status);
  }
  status.textContent = message;
  status.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { status.hidden = true; }, 3500);
}
function syncFavorites(scope = document) {
  scope.querySelectorAll('[data-ap-save]').forEach(button => {
    const active = saved.includes(button.dataset.apSave);
    button.hidden = false;
    button.setAttribute('aria-pressed', String(active));
    button.setAttribute('aria-label', `${active ? 'Unsave' : 'Save'} ${button.dataset.productTitle || 'this product'}`);
    const label = button.querySelector('[data-ap-save-label]');
    if (label) label.textContent = active ? 'Saved' : 'Save for later';
  });
  document.querySelectorAll('[data-ap-saved-count]').forEach(node => {
    node.textContent = String(saved.length);
    node.hidden = !saved.length;
  });
}
function setSaved(next) {
  try { localStorage.setItem(storageKey, JSON.stringify(next)); }
  catch { announce('Favorites could not be saved. Check this browser’s storage settings.'); return false; }
  saved = next;
  syncFavorites();
  return true;
}
function money(cents) {
  try { return new Intl.NumberFormat(config.locale || document.documentElement.lang || 'en-US', {style:'currency',currency:config.currency || 'USD'}).format(Number(cents) / 100); }
  catch { return (Number(cents) / 100).toFixed(2) + ' ' + (config.currency || 'USD'); }
}
function make(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}
const heartMarkup = '<svg class="ap-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z"/></svg>';
function savedCard(product, handle) {
  const card = make('article','ap-product');
  card.dataset.apProduct = handle;
  const media = make('div','ap-product-media');
  const link = make('a','ap-product-open');
  link.href = apiUrl(`products/${encodeURIComponent(handle)}`);
  link.setAttribute('aria-label', product.title);
  if (product.featured_image) {
    const image = make('img','ap-product-photo');
    const imageUrl = new URL(product.featured_image, window.location.origin);
    if (imageUrl.protocol === 'https:' || imageUrl.origin === location.origin) image.src = imageUrl.toString();
    image.alt = product.title;
    image.loading = 'lazy'; image.width = 600; image.height = 600;
    link.append(image);
  }
  media.append(link);
  const save = make('button','ap-icon-button ap-favorite');
  save.type = 'button'; save.dataset.apSave = handle; save.dataset.productTitle = product.title;
  save.innerHTML = heartMarkup; // Constant SVG only; product data is always textContent.
  media.append(save);
  if (!product.available) media.append(make('span','ap-card-badge','Sold out'));
  const title = make('a','ap-product-name',product.title); title.href = link.href;
  const price = make('div','ap-product-price',`${product.price_varies ? 'From ' : ''}${money(product.price_min ?? product.price)}`);
  card.append(media,title,make('p','ap-product-kind',product.type || ''),price);
  return card;
}
let savedGeneration = 0;
async function renderSaved(reset = true) {
  const page = document.querySelector('[data-ap-saved-page]');
  if (!page) return;
  const generation = ++savedGeneration;
  const grid = page.querySelector('[data-ap-saved-grid]');
  const status = page.querySelector('[data-ap-saved-status]');
  const empty = page.querySelector('[data-ap-saved-empty]');
  const more = page.querySelector('[data-ap-saved-more]');
  const retry = page.querySelector('[data-ap-saved-retry]');
  if (reset) { grid.replaceChildren(); page.dataset.shown = '0'; }
  const start = Number(page.dataset.shown || 0);
  const currentHandles = saved.slice(start, start + 24);
  empty.hidden = saved.length !== 0;
  retry.hidden = true; more.hidden = true;
  status.textContent = saved.length ? 'Checking prices and availability…' : 'No favorites saved yet.';
  let unavailable = 0, failed = 0;
  for (let at = 0; at < currentHandles.length; at += 6) {
    const batch = currentHandles.slice(at, at + 6);
    const products = await Promise.all(batch.map(async handle => {
      try {
        const response = await fetch(apiUrl(`products/${encodeURIComponent(handle)}.js`), {headers:{Accept:'application/json'}});
        if (response.status === 404) return {handle, unavailable:true};
        if (!response.ok) throw new Error('Product unavailable');
        return {handle, product:await response.json()};
      } catch { return {handle, failed:true}; }
    }));
    if (generation !== savedGeneration || !page.isConnected) return;
    for (const item of products) {
      if (item.unavailable) unavailable++;
      else if (item.failed) failed++;
      else if (item.product && typeof item.product.title === 'string') grid.append(savedCard(item.product, item.handle));
    }
  }
  if (generation !== savedGeneration) return;
  const shown = Math.min(saved.length, start + currentHandles.length);
  page.dataset.shown = String(shown);
  more.hidden = shown >= saved.length;
  retry.hidden = failed === 0;
  status.textContent = `${saved.length} saved ${saved.length === 1 ? 'favorite' : 'favorites'}.${unavailable ? ` ${unavailable} ${unavailable === 1 ? 'item is' : 'items are'} no longer available.` : ''}${failed ? ` ${failed} ${failed === 1 ? 'item could' : 'items could'} not be loaded. Try again.` : ''}`;
  syncFavorites(grid);
}
function setupPredictive(scope = document) {
  scope.querySelectorAll('[data-ap-predictive-form]').forEach(form => {
    if (form.dataset.ready) return;
    form.dataset.ready = 'true';
    const input = form.querySelector('[data-ap-predictive-input]');
    const shell = form.parentElement;
    const panel = shell.querySelector('[data-ap-predictive-results]');
    const status = shell.querySelector('[data-ap-search-status]');
    let timer, controller, requestVersion = 0;
    const close = () => { panel.hidden = true; };
    input.addEventListener('input', () => {
      clearTimeout(timer); controller?.abort(); requestVersion++;
      const query = input.value.trim();
      if (query.length < 2) { panel.replaceChildren(); close(); status.textContent = ''; return; }
      const version = requestVersion;
      timer = setTimeout(async () => {
        controller = new AbortController();
        const url = new URL(config.predictive || apiUrl('search/suggest'), location.origin);
        url.pathname = url.pathname.replace(/\.json$/, '') + '.json';
        url.searchParams.set('q',query);
        url.searchParams.set('resources[type]','product');
        url.searchParams.set('resources[limit]','5');
        url.searchParams.set('resources[options][unavailable_products]','last');
        try {
          const response = await fetch(url, {signal:controller.signal,headers:{Accept:'application/json'}});
          if (!response.ok) throw new Error('Search unavailable');
          const body = await response.json();
          if (version !== requestVersion || input.value.trim() !== query) return;
          const products = body.resources?.results?.products || [];
          panel.replaceChildren();
          panel.append(make('p','ap-eyebrow','Products'));
          products.forEach(product => {
            const link = make('a','ap-mini-result');
            const productUrl = new URL(product.url,location.origin);
            if (productUrl.origin !== location.origin) return;
            link.href = productUrl.toString();
            const src = typeof product.featured_image === 'string' ? product.featured_image : product.featured_image?.url;
            if (src) {
              const imageUrl = new URL(src,location.origin);
              if (imageUrl.protocol === 'https:' || imageUrl.origin === location.origin) {
                const image = make('img'); image.src = imageUrl.toString(); image.alt = ''; image.width = 50; image.height = 50; link.append(image);
              }
            }
            const words = make('span',null,product.title);
            words.append(make('small',null,product.type || 'View product'));
            link.append(words); panel.append(link);
          });
          if (!products.length) panel.append(make('p','ap-search-status','No suggestions yet. Search the full catalog or try another phrase.'));
          const all = make('a','ap-predictive-all','See all results →');
          const searchUrl = new URL(config.search || apiUrl('search'),location.origin);
          searchUrl.searchParams.set('q',query); searchUrl.searchParams.set('type','product');
          all.href = searchUrl.toString(); panel.append(all);
          panel.hidden = false;
          status.textContent = products.length ? `${products.length} product suggestions available. Tab to browse them.` : 'No product suggestions. You can still search the full catalog.';
        } catch (error) {
          if (error.name === 'AbortError' || version !== requestVersion) return;
          close(); status.textContent = 'Suggestions are unavailable. Press Enter to search the full catalog.';
        }
      }, 250);
    });
    input.addEventListener('keydown',event => {
      if (event.key === 'Escape') { close(); controller?.abort(); requestVersion++; }
      if (event.key === 'ArrowDown' && !panel.hidden) { const first = panel.querySelector('a'); if (first) { event.preventDefault(); first.focus(); } }
    });
    panel.addEventListener('keydown',event => {
      const links = [...panel.querySelectorAll('a')], index = links.indexOf(document.activeElement);
      if (event.key === 'Escape') { close(); input.focus(); }
      if (event.key === 'ArrowDown') { event.preventDefault(); links[(index + 1) % links.length]?.focus(); }
      if (event.key === 'ArrowUp') { event.preventDefault(); index <= 0 ? input.focus() : links[index - 1]?.focus(); }
    });
    shell.addEventListener('focusout',() => setTimeout(() => { if (!shell.contains(document.activeElement)) close(); },0));
    document.addEventListener('pointerdown',event => { if (!shell.contains(event.target)) close(); });
  });
}
function setupArtwork(scope = document) {
  scope.querySelectorAll('[data-ap-artwork-control]').forEach(label => { label.hidden = false; });
}
function setupHeader(scope = document) {
  const header = scope.querySelector('[data-ap-header]');
  if (!header || header.dataset.measured) return;
  header.dataset.measured = 'true';
  const measure = () => {
    const group = document.getElementById('header-group');
    document.documentElement.style.setProperty('--header-height',header.getBoundingClientRect().height+'px');
    document.documentElement.style.setProperty('--header-group-height',(group?.getBoundingClientRect().height || header.getBoundingClientRect().height)+'px');
  };
  new ResizeObserver(measure).observe(header); measure();
}
async function syncCartCount() {
  try {
    const response = await fetch(apiUrl('cart.js'), {headers:{Accept:'application/json'}});
    if (!response.ok) return;
    const cart = await response.json();
    document.querySelectorAll('[data-ap-cart-count]').forEach(node => { node.textContent = String(cart.item_count); });
    document.querySelectorAll('[data-ap-cart-open]').forEach(node => node.setAttribute('aria-label',`Shopping bag, ${cart.item_count} items`));
  } catch { /* Keep the server-rendered count if the network is unavailable. */ }
}
if (StandardEvents.productSelect) document.addEventListener(StandardEvents.productSelect,event => {
  Promise.resolve(event.promise).then(() => requestAnimationFrame(() => initialize())).catch(() => {});
});
document.addEventListener(StandardEvents.cartLinesUpdate,event => {
  Promise.resolve(event.promise).catch(() => null).finally(syncCartCount);
});
// Store-upkeep action item 39 (his shop notes of 2026-10-29): on a phone an add to cart changed only the header's count
// (the button's brief "Added" and the picture flying to the bag; no drawer opens, auto-open is off), and the header may be
// scrolled away. A phone now gets a short note at the foot of the screen, "Added · View cart", for a few seconds; View cart
// opens the cart drawer (the cart page without the script). A desktop is unchanged. Screen readers already hear the theme's
// own "Added" (product-form.js's live region), so the note is not a second live region.
const phoneAdded = window.matchMedia('(max-width: 749px)');
let addedNote = null;
let addedTimer = 0;
const showAdded = () => {
  if (!addedNote) {
    addedNote = document.createElement('div');
    addedNote.className = 'ap-added';
    addedNote.hidden = true;
    const cartUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || apiUrl('cart');
    addedNote.innerHTML = '<span class="ap-added__done">Added</span><span aria-hidden="true"> · </span>'
      + `<a class="ap-added__link" href="${cartUrl}" data-ap-added-cart>View cart</a>`;
    document.body.appendChild(addedNote);
  }
  addedNote.hidden = false;
  clearTimeout(addedTimer);
  addedTimer = setTimeout(() => { addedNote.hidden = true; }, 4000);
};
document.addEventListener(StandardEvents.cartLinesUpdate,event => {
  if (event.action !== 'add' || !phoneAdded.matches) return;
  Promise.resolve(event.promise).then(result => {
    if (result && result.detail && result.detail.didError) return;
    showAdded();
  }).catch(() => {});
});
document.addEventListener('click',event => {
  const link = event.target.closest('[data-ap-added-cart]');
  if (!link) return;
  if (addedNote) addedNote.hidden = true;
  const drawer = document.getElementById('cart-drawer');
  if (typeof drawer?.open === 'function' && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) {
    event.preventDefault();
    drawer.open();
  }
});
document.addEventListener('click',event => {
  const button = event.target.closest('[data-ap-save]');
  if (button) {
    const handle = button.dataset.apSave;
    if (!handlePattern.test(handle)) return;
    const exists = saved.includes(handle);
    if (!exists && saved.length >= limit) { announce(`You can keep up to ${limit} favorites. Remove one to save another.`); return; }
    if (setSaved(exists ? saved.filter(h => h !== handle) : [...saved,handle])) {
      announce(exists ? 'Removed from your favorites.' : 'Saved for later in this browser.');
      if (document.querySelector('[data-ap-saved-page]')) renderSaved();
    }
    return;
  }
  const opener = event.target.closest('[data-ap-dialog]');
  if (opener) {
    const dialog = document.getElementById(opener.dataset.apDialog);
    if (dialog instanceof HTMLDialogElement) { dialog.showModal(); }
  }
  const close = event.target.closest('[data-ap-close]');
  if (close) close.closest('dialog')?.close();
  const cartLink = event.target.closest('[data-ap-cart-open]');
  if (cartLink && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) {
    const drawer = document.getElementById('cart-drawer');
    if (typeof drawer?.open === 'function') { event.preventDefault(); drawer.open(); }
  }
  if (event.target.closest('[data-ap-saved-more]')) renderSaved(false);
  if (event.target.closest('[data-ap-saved-retry]')) renderSaved(true);
});
document.addEventListener('change',event => {
  if (event.target.matches('[data-ap-artwork-toggle]')) {
    event.target.closest('[data-ap-artwork-scope]')?.setAttribute('data-artwork-view',String(event.target.checked));
  }
});
document.addEventListener('submit',event => {
  const form = event.target.closest('[data-ap-gift-finder]');
  if (!form) return;
  const recipient = form.querySelector('[data-ap-gift-recipient]');
  const target = new URL(recipient?.value || form.action,location.origin);
  if (target.origin !== location.origin) return;
  event.preventDefault();
  const data = new FormData(form);
  for (const [key,value] of data.entries()) if (value) target.searchParams.set(key,String(value));
  location.assign(target.toString());
});
document.addEventListener('keydown',event => {
  if (event.key === 'Escape') document.querySelectorAll('[data-ap-mega-menu][open]').forEach(node => { node.open = false; node.querySelector('summary')?.focus(); });
});
document.addEventListener('pointerdown',event => {
  document.querySelectorAll('[data-ap-mega-menu][open]').forEach(node => { if (!node.contains(event.target)) node.open = false; });
});

function setupLatest(scope = document) {
  scope.querySelectorAll('[data-ap-latest-url]').forEach(grid => {
    if (grid.dataset.latestReady) return;
    grid.dataset.latestReady = 'true';
    const load = async () => {
      try {
        const url = new URL(grid.dataset.apLatestUrl, location.origin);
        if (url.origin !== location.origin) return;
        const response = await fetch(url, {headers:{Accept:'text/html'}});
        if (!response.ok) return;
        const html = new DOMParser().parseFromString(await response.text(),'text/html');
        const next = html.querySelector('[data-ap-latest-grid]');
        if (!next || !grid.isConnected) return;
        grid.replaceChildren(...next.children);
        syncFavorites(grid);
      } catch { /* Retain the server-rendered featured selection on a slow or unavailable connection. */ }
    };
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => { if (entries.some(entry => entry.isIntersecting)) { observer.disconnect(); load(); } },{rootMargin:'400px'});
      observer.observe(grid);
    } else load();
  });
}
function initialize(scope = document) { setupLatest(scope); syncFavorites(scope); setupPredictive(scope); setupArtwork(scope); setupHeader(scope); }
initialize(); renderSaved();
document.addEventListener('shopify:section:load',event => initialize(event.target));
window.addEventListener('storage',event => { if (event.key === storageKey) { saved = readSaved(); syncFavorites(); renderSaved(); } });
window.addEventListener('pageshow',event => { if (event.persisted) { saved = readSaved(); syncFavorites(); syncCartCount(); renderSaved(); } });
